import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Card, Row, Col, Form, Button, Table, Alert, Spinner, Tabs, Tab, Badge, Modal } from 'react-bootstrap';
import { Bar, Pie } from 'react-chartjs-2';
import { FaFilePdf, FaFilter, FaUserFriends, FaMapMarkedAlt, FaClock, FaFileAlt, FaDownload, FaEye, FaFileExcel, FaSearch, FaTimes } from 'react-icons/fa';
import { statisticsAPI, personnelAPI, sitesAPI, reportsAPI, branchesAPI, regionsAPI } from '../../utils/api';
import { format } from 'date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { API_BASE_URL } from '../../config';
import './Statistics.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AdminStatistics = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('statistics');
  const [timeRange, setTimeRange] = useState('week');
  const [searchParams] = useSearchParams();
  const [selectedPersonnel, setSelectedPersonnel] = useState('all');
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [branches, setBranches] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filteredRegions, setFilteredRegions] = useState([]);
  const [customDateRange, setCustomDateRange] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );

  // Monitor theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // Fetch branches and regions on mount
  // Backend automatically filters by authenticated user's company via JWT token
  useEffect(() => {
    const fetchStructure = async () => {
      try {
        const [branchesRes, regionsRes] = await Promise.all([
          branchesAPI.getAll(),
          regionsAPI.getAll()
        ]);
        setBranches(branchesRes.data);
        setRegions(regionsRes.data);
      } catch (err) {
        console.error("Failed to fetch structure data:", err);
      }
    };
    fetchStructure();
  }, []);

  // Filter regions when branch changes.
  // FIX: Previously this unconditionally reset selectedRegion to 'all' any time
  // selectedBranch changed. That silently discarded a region the admin had
  // already picked (e.g. region chosen first, branch chosen second — or
  // re-picking the same branch), so the query ended up branch-only and
  // returned every site in the branch instead of the intersection the admin
  // actually asked for. Now we only clear the region when it no longer
  // belongs to the newly selected branch, so a compatible branch+region
  // combination survives regardless of the order they were picked in.
  useEffect(() => {
    if (selectedBranch !== 'all') {
      const regionsInBranch = regions.filter(r => r.branchId === parseInt(selectedBranch));
      setFilteredRegions(regionsInBranch);
      setSelectedRegion(prevRegion => {
        if (prevRegion === 'all') return prevRegion;
        const stillValid = regionsInBranch.some(r => r.id === parseInt(prevRegion));
        return stillValid ? prevRegion : 'all';
      });
    } else {
      setFilteredRegions(regions);
    }
  }, [selectedBranch, regions]);

  // Helper function to get chart colors based on theme
  const getChartColors = () => {
    if (isDarkMode) {
      return {
        textColor: '#F8FAFC',
        gridColor: 'rgba(148, 163, 184, 0.2)',
        borderColor: 'rgba(148, 163, 184, 0.3)',
      };
    }
    return {
      textColor: '#1E293B',
      gridColor: 'rgba(15, 23, 42, 0.1)',
      borderColor: 'rgba(15, 23, 42, 0.2)',
    };
  };

  const chartColors = getChartColors();

  const [personnel, setPersonnel] = useState([]);
  const [sites, setSites] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [reportsData, setReportsData] = useState([]);
  const [unvisitedSites, setUnvisitedSites] = useState([]);
  // Site visit status filter for Sites Statistics tab: 'all' | 'visited' | 'unvisited'
  const [siteVisitStatus, setSiteVisitStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  // NEW: Specialized export loading states
  const [executiveSummaryLoading, setExecutiveSummaryLoading] = useState(false);
  const [timesheetLoading, setTimesheetLoading] = useState(false);
  const [analyticsExcelLoading, setAnalyticsExcelLoading] = useState(false);

  // DEPRECATED: Old basic export states (keeping for backward compatibility)
  const [exportLoading, setExportLoading] = useState(false);
  const [excelExportLoading, setExcelExportLoading] = useState(false);
  const [exportError, setExportError] = useState('');

  // Reports tab export states
  const [reportExportLoading, setReportExportLoading] = useState(false);
  const [reportExportError, setReportExportError] = useState('');

  // Personnel Performance export states
  const [personnelPerformanceExportLoading, setPersonnelPerformanceExportLoading] = useState(false);
  const [personnelPerformanceExportError, setPersonnelPerformanceExportError] = useState('');

  const [selectedReport, setSelectedReport] = useState(null);
  const [personnelPerformanceData, setPersonnelPerformanceData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sitesReportsExportLoading, setSitesReportsExportLoading] = useState(false);
  const [sitesReportsExportError, setSitesReportsExportError] = useState('');
  const [unvisitedExportLoading, setUnvisitedExportLoading] = useState(false);
  const [unvisitedExportError, setUnvisitedExportError] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showActivityReportModal, setShowActivityReportModal] = useState(false);
  const [summaryStats, setSummaryStats] = useState({
    totalHours: 0,
    totalVisits: 0,
    totalReports: 0,
    averageSessionTime: 0
  });
  const [chartData, setChartData] = useState({
    activity: {},
    siteDistribution: {},
    hoursByPersonnel: {}
  });

  const [showBreaksModal, setShowBreaksModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedBreakActivity, setSelectedBreakActivity] = useState(null);
  const [selectedTrackingActivity, setSelectedTrackingActivity] = useState(null);

  // Live search queries for Statistics and Reports tabs
  const [statisticsSearchQuery, setStatisticsSearchQuery] = useState('');
  const [reportsSearchQuery, setReportsSearchQuery] = useState('');

  // Function to process personnel performance data
  const processPersonnelPerformanceData = (activityData) => {
    const personnelMap = {};

    activityData.forEach(activity => {
      const personnelId = activity.personnelId;
      if (!personnelMap[personnelId]) {
        personnelMap[personnelId] = {
          id: personnelId,
          name: activity.personnelName,
          position: activity.position,
          totalVisits: 0,
          totalHours: 0,
          totalReports: 0
        };
      }

      personnelMap[personnelId].totalVisits += 1;
      if (activity.hoursSpent) {
        personnelMap[personnelId].totalHours += parseFloat(activity.hoursSpent);
      }
      if (activity.reportSubmitted) {
        personnelMap[personnelId].totalReports += 1;
      }
    });

    // Convert to array and add performance ratings
    return Object.values(personnelMap).map(personnel => {
      const avgHoursPerVisit = personnel.totalVisits > 0 ? (personnel.totalHours / personnel.totalVisits).toFixed(1) : '0.0';
      let performanceRating = 'Good';
      let ratingColor = 'success';

      // Performance rating logic
      if (personnel.totalHours >= 40 && personnel.totalReports >= personnel.totalVisits * 0.8) {
        performanceRating = 'Excellent';
        ratingColor = 'success';
      } else if (personnel.totalHours >= 20 && personnel.totalReports >= personnel.totalVisits * 0.6) {
        performanceRating = 'Good';
        ratingColor = 'success';
      } else if (personnel.totalHours >= 10) {
        performanceRating = 'Fair';
        ratingColor = 'warning';
      } else {
        performanceRating = 'Needs Improvement';
        ratingColor = 'danger';
      }

      return {
        ...personnel,
        avgHoursPerVisit: parseFloat(avgHoursPerVisit),
        performanceRating,
        ratingColor
      };
    }).sort((a, b) => b.totalHours - a.totalHours); // Sort by total hours descending
  };

  // Debug: Log active tab changes
  useEffect(() => {
    console.log('Active tab changed to:', activeTab);
  }, [activeTab]);

  useEffect(() => {
    const personnelId = searchParams.get('personnelId');
    if (personnelId && personnelId !== selectedPersonnel) {
      setSelectedPersonnel(personnelId);
    }
    setInitialLoad(false);
  }, []);

  useEffect(() => {
    if (initialLoad) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        const personnelResponse = await personnelAPI.getAll();
        const fetchedPersonnel = personnelResponse.data;

        const sitesResponse = await sitesAPI.getAll();
        const fetchedSites = sitesResponse.data;

        const filters = {
          timeRange: customDateRange ? 'custom' : timeRange,
          personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
          siteId: selectedSite !== 'all' ? selectedSite : null,
          branchId: selectedBranch !== 'all' ? selectedBranch : null,
          regionId: selectedRegion !== 'all' ? selectedRegion : null,
          startDate: customDateRange ? startDate : null,
          endDate: customDateRange ? endDate : null
        };

        // Update URL if personnel is selected
        if (selectedPersonnel !== 'all') {
          navigate(`/admin/statistics?personnelId=${selectedPersonnel}`, { replace: true });
        } else if (searchParams.get('personnelId')) {
          // Clear the personnelId from URL if 'all' is selected
          navigate('/admin/statistics', { replace: true });
        }

        const activityResponse = await statisticsAPI.getActivity(filters);
        const fetchedActivity = activityResponse.data || [];

        const summaryResponse = await statisticsAPI.getSummary(filters);
        const fetchedSummary = summaryResponse.data || {
          totalHours: 0,
          totalVisits: 0,
          totalReports: 0,
          averageSessionTime: 0
        };

        const chartResponse = await statisticsAPI.getChartData(filters);
        const fetchedChartData = chartResponse.data || {
          activity: {},
          siteDistribution: {},
          hoursByPersonnel: {}
        };

        // Debug: Log chart data
        console.log('Chart Data Response:', fetchedChartData);
        console.log('Activity Chart Data:', fetchedChartData.activity);

        const reportsResponse = await reportsAPI.getAllReports(filters);
        const fetchedReports = reportsResponse.data || [];

        setPersonnel(fetchedPersonnel);
        setSites(fetchedSites);
        setActivityData(fetchedActivity);
        setSummaryStats(fetchedSummary);
        setChartData(fetchedChartData);
        setReportsData(fetchedReports);

        // Process personnel performance data
        const processedPersonnelData = processPersonnelPerformanceData(fetchedActivity);
        setPersonnelPerformanceData(processedPersonnelData);

        if (activeTab === 'unvisited') {
          const unvisitedResponse = await statisticsAPI.getUnvisitedSites(filters);
          setUnvisitedSites(unvisitedResponse.data.sites || []);
        }
      } catch (error) {
        console.error("Failed to fetch statistics data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange, selectedPersonnel, selectedSite, selectedBranch, selectedRegion, activeTab, customDateRange, startDate, endDate, initialLoad]);

  // Apply visited / unvisited / all filter on top of the personnel-scoped sites
  // The backend already restricts the site list to:
  //   - sites assigned to the selected personnel (when a personnel is selected), or
  //   - all sites within the admin's scope (when personnel is 'all').
  // Each site comes with `visitCount` for the selected date range, also already
  // scoped to the selected personnel when one is chosen.
  const displayedSites = unvisitedSites.filter((site) => {
    const count = Number(site.visitCount) || 0;
    if (siteVisitStatus === 'visited') return count > 0;
    if (siteVisitStatus === 'unvisited') return count === 0;
    return true; // 'all'
  });

  const visitedCount = unvisitedSites.filter((s) => (Number(s.visitCount) || 0) > 0).length;
  const unvisitedCount = unvisitedSites.filter((s) => (Number(s.visitCount) || 0) === 0).length;

  // Pagination logic
  const totalPages = Math.ceil(personnelPerformanceData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPersonnelData = personnelPerformanceData.slice(startIndex, endIndex);

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [personnelPerformanceData.length]);

  // Handle showing breaks modal
  const handleShowBreaks = (activity) => {
    console.log('handleShowBreaks called with:', activity);
    setSelectedBreakActivity(activity);
    setShowBreaksModal(true);
  };

  // Handle showing tracking modal
  const handleShowTracking = (activity) => {
    console.log('handleShowTracking called with:', activity);
    setSelectedTrackingActivity(activity);
    setShowTrackingModal(true);
  };

  // ===== NEW SPECIALIZED EXPORT HANDLERS =====

  // Handle Executive Summary PDF export
  const handleExportExecutiveSummary = async () => {
    setExecutiveSummaryLoading(true);
    setExportError('');

    try {
      const filters = {
        timeRange: customDateRange ? 'custom' : timeRange,
        personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
        siteId: selectedSite !== 'all' ? selectedSite : null,
        // FIX: the backend (exportExecutiveSummary) filters by branch/region,
        // but these were never being sent, so the export always covered every
        // branch/region regardless of what was selected on screen.
        branchId: selectedBranch !== 'all' ? selectedBranch : null,
        regionId: selectedRegion !== 'all' ? selectedRegion : null,
        startDate: customDateRange ? startDate : null,
        endDate: customDateRange ? endDate : null
      };

      const response = await statisticsAPI.exportExecutiveSummary(filters);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `executive_summary_${timeRange}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Executive Summary:', error);
      setExportError('Failed to export Executive Summary. Please try a different time range or contact support.');
    } finally {
      setExecutiveSummaryLoading(false);
    }
  };

  // Handle Timesheet Excel export
  const handleExportTimesheet = async () => {
    setTimesheetLoading(true);
    setExportError('');

    try {
      const filters = {
        timeRange: customDateRange ? 'custom' : timeRange,
        personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
        startDate: customDateRange ? startDate : null,
        endDate: customDateRange ? endDate : null
      };

      const response = await statisticsAPI.exportTimesheet(filters);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `timesheet_${timeRange}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Timesheet:', error);
      setExportError('Failed to export Timesheet. Please try a different time range or contact support.');
    } finally {
      setTimesheetLoading(false);
    }
  };

  // Handle Analytics Dashboard Excel export (renamed from handleExportExcel)
  const handleExportAnalyticsDashboard = async () => {
    setAnalyticsExcelLoading(true);
    setExportError('');

    try {
      const filters = {
        timeRange: customDateRange ? 'custom' : timeRange,
        personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
        siteId: selectedSite !== 'all' ? selectedSite : null,
        // FIX: the backend (exportExcel -> getComprehensiveStatsForExport) filters
        // every sheet, including "Site Performance", by branch/region — but these
        // were never sent from here, so the workbook always included every site
        // regardless of the Branch/Region dropdowns selected on screen.
        branchId: selectedBranch !== 'all' ? selectedBranch : null,
        regionId: selectedRegion !== 'all' ? selectedRegion : null,
        startDate: customDateRange ? startDate : null,
        endDate: customDateRange ? endDate : null
      };

      const response = await statisticsAPI.exportExcel(filters);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_dashboard_${timeRange}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Analytics Dashboard:', error);
      setExportError('Failed to export Analytics Dashboard. Please try a different time range or contact support.');
    } finally {
      setAnalyticsExcelLoading(false);
    }
  };

  // ===== DEPRECATED OLD EXPORT HANDLERS (keeping for backward compatibility) =====

  // Handle PDF export for statistics
  const handleExportPDF = async () => {
    setExportLoading(true);
    setExportError('');

    try {
      // Prepare filters for export
      const filters = {
        timeRange: customDateRange ? 'custom' : timeRange,
        personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
        siteId: selectedSite !== 'all' ? selectedSite : null,
        startDate: customDateRange ? startDate : null,
        endDate: customDateRange ? endDate : null
      };

      // Call the export API with the current filters
      const response = await statisticsAPI.exportPdf(filters);

      // The response is already a blob since we specified responseType: 'blob' in the API call
      const blob = response.data;

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `admin_statistics_${timeRange}_${new Date().toISOString().split('T')[0]}.pdf`);

      // Append the link to the body
      document.body.appendChild(link);

      // Click the link to download the file
      link.click();

      // Remove the link from the body
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      setExportError('Failed to export PDF. Please try a different time range or contact support.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExcelExportLoading(true);
    setExportError('');

    try {
      const filters = {
        timeRange: customDateRange ? 'custom' : timeRange,
        personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
        siteId: selectedSite !== 'all' ? selectedSite : null,
        startDate: customDateRange ? startDate : null,
        endDate: customDateRange ? endDate : null
      };

      const response = await statisticsAPI.exportExcel(filters);

      const blob = response.data;

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `admin_statistics_${timeRange}_${new Date().toISOString().split('T')[0]}.xlsx`);

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Excel:', error);
      setExportError('Failed to export Excel. Please try a different time range or contact support.');
    } finally {
      setExcelExportLoading(false);
    }
  };

  // Handle PDF export for reports
  const handleExportReportPDF = async (reportId) => {
    setReportExportLoading(true);
    setReportExportError('');

    try {
      // Call the export API with the report ID
      const response = await reportsAPI.exportReportPdf(reportId);

      // The response is already a blob since we specified responseType: 'blob' in the API call
      const blob = response.data;

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${reportId}_${new Date().toISOString().split('T')[0]}.pdf`);

      // Append the link to the body
      document.body.appendChild(link);

      // Click the link to download the file
      link.click();

      // Remove the link from the body
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export report PDF:', error);
      setReportExportError('Failed to export report PDF. Please try again.');
    } finally {
      setReportExportLoading(false);
    }
  };

  // Handle Excel export for Personnel Performance Summary
  const handleExportPersonnelPerformanceExcel = async () => {
    setPersonnelPerformanceExportLoading(true);
    setPersonnelPerformanceExportError('');

    try {
      // Prepare filters for export
      const filters = {
        timeRange: customDateRange ? 'custom' : timeRange,
        personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
        siteId: selectedSite !== 'all' ? selectedSite : null,
        startDate: customDateRange ? startDate : null,
        endDate: customDateRange ? endDate : null
      };

      // Call the export Personnel Performance Excel API
      const response = await statisticsAPI.exportPersonnelPerformanceExcel(filters);

      // The response is already a blob since we specified responseType: 'blob' in the API call
      const blob = response.data;

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `personnel_performance_summary_${timeRange || 'custom'}_${new Date().toISOString().split('T')[0]}.xlsx`);

      // Append the link to the body
      document.body.appendChild(link);

      // Click the link to download the file
      link.click();

      // Remove the link from the body
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Personnel Performance Excel:', error);
      setPersonnelPerformanceExportError('Failed to export Personnel Performance Excel. Please try again.');
    } finally {
      setPersonnelPerformanceExportLoading(false);
    }
  };

  // Handle PDF export for Personnel Performance Summary
  const handleExportPersonnelPerformancePdf = async () => {
    setPersonnelPerformanceExportLoading(true);
    setPersonnelPerformanceExportError('');

    try {
      // Prepare filters for export
      const filters = {
        timeRange: customDateRange ? 'custom' : timeRange,
        personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
        siteId: selectedSite !== 'all' ? selectedSite : null,
        startDate: customDateRange ? startDate : null,
        endDate: customDateRange ? endDate : null
      };

      // Call the export Personnel Performance PDF API
      const response = await statisticsAPI.exportPersonnelPerformancePdf(filters);

      // The response is already a blob since we specified responseType: 'blob' in the API call
      const blob = response.data;

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `personnel_performance_summary_${timeRange || 'custom'}_${new Date().toISOString().split('T')[0]}.pdf`);

      // Append the link to the body
      document.body.appendChild(link);

      // Click the link to download the file
      link.click();

      // Remove the link from the body
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Personnel Performance PDF:', error);
      setPersonnelPerformanceExportError('Failed to export Personnel Performance PDF. Please try again.');
    } finally {
      setPersonnelPerformanceExportLoading(false);
    }
  };

  // Handle Sites Reports PDF export
  const handleExportSitesReportsPdf = async () => {
    setSitesReportsExportLoading(true);
    setSitesReportsExportError('');

    try {
      // Prepare filters for export
      const filters = {
        timeRange: customDateRange ? 'custom' : timeRange,
        personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
        siteId: selectedSite !== 'all' ? selectedSite : null,
        startDate: customDateRange ? startDate : null,
        endDate: customDateRange ? endDate : null
      };

      // Call the export Sites Reports PDF API
      const response = await reportsAPI.exportSitesReportsPdf(filters);

      // The response is already a blob since we specified responseType: 'blob' in the API call
      const blob = response.data;

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sites_reports_${timeRange || 'custom'}_${new Date().toISOString().split('T')[0]}.pdf`);

      // Append the link to the body
      document.body.appendChild(link);

      // Click the link to download the file
      link.click();

      // Remove the link from the body
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Sites Reports PDF:', error);
      setSitesReportsExportError('Failed to export Sites Reports PDF. Please try again.');
    } finally {
      setSitesReportsExportLoading(false);
    }
  };

  // Handle Sites Reports Excel export
  const handleExportSitesReportsExcel = async () => {
    setSitesReportsExportLoading(true);
    setSitesReportsExportError('');

    try {
      // Prepare filters for export
      const filters = {
        timeRange: customDateRange ? 'custom' : timeRange,
        personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
        siteId: selectedSite !== 'all' ? selectedSite : null,
        startDate: customDateRange ? startDate : null,
        endDate: customDateRange ? endDate : null
      };

      // Call the export Sites Reports Excel API
      const response = await reportsAPI.exportSitesReportsExcel(filters);

      // The response is already a blob since we specified responseType: 'blob' in the API call
      const blob = response.data;

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sites_reports_${timeRange || 'custom'}_${new Date().toISOString().split('T')[0]}.xlsx`);

      // Append the link to the body
      document.body.appendChild(link);

      // Click the link to download the file
      link.click();

      // Remove the link from the body
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Sites Reports Excel:', error);
      setSitesReportsExportError('Failed to export Sites Reports Excel. Please try again.');
    } finally {
      setSitesReportsExportLoading(false);
    }
  };

  // Handle Unvisited Sites PDF export
  const handleExportUnvisitedSitesPdf = async () => {
    setUnvisitedExportLoading(true);
    setUnvisitedExportError('');

    try {
      const filters = {
        timeRange: customDateRange ? 'custom' : timeRange,
        personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
        siteId: selectedSite !== 'all' ? selectedSite : null,
        branchId: selectedBranch !== 'all' ? selectedBranch : null,
        regionId: selectedRegion !== 'all' ? selectedRegion : null,
        visitStatus: siteVisitStatus,
        startDate: customDateRange ? startDate : null,
        endDate: customDateRange ? endDate : null
      };

      const response = await statisticsAPI.exportUnvisitedSitesPdf(filters);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `unvisited_sites_${timeRange}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Unvisited Sites PDF:', error);
      setUnvisitedExportError('Failed to export Unvisited Sites PDF. Please try again.');
    } finally {
      setUnvisitedExportLoading(false);
    }
  };

  // Handle Unvisited Sites Excel export
  const handleExportUnvisitedSitesExcel = async () => {
    setUnvisitedExportLoading(true);
    setUnvisitedExportError('');

    try {
      const filters = {
        timeRange: customDateRange ? 'custom' : timeRange,
        personnelId: selectedPersonnel !== 'all' ? selectedPersonnel : null,
        siteId: selectedSite !== 'all' ? selectedSite : null,
        branchId: selectedBranch !== 'all' ? selectedBranch : null,
        regionId: selectedRegion !== 'all' ? selectedRegion : null,
        visitStatus: siteVisitStatus,
        startDate: customDateRange ? startDate : null,
        endDate: customDateRange ? endDate : null
      };

      const response = await statisticsAPI.exportUnvisitedSitesExcel(filters);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `unvisited_sites_${timeRange}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Unvisited Sites Excel:', error);
      setUnvisitedExportError('Failed to export Unvisited Sites Excel. Please try again.');
    } finally {
      setUnvisitedExportLoading(false);
    }
  };

  // Handle view report
  const handleViewReport = (report) => {
    console.log('handleViewReport called with:', report);
    navigate(`/admin/reports/${report.id}`);
  };


  // Handle view activity report
  const handleViewActivityReport = async (activity) => {
    console.log('handleViewActivityReport called with:', activity);
    try {
      setSelectedActivity(activity);

      // fetch full report using reportId
      if (activity.reportId) {
        const { data } = await reportsAPI.getById(activity.reportId);
        setSelectedReport(data); // ✅ now contains checkOutPhotos
      }

      setShowActivityReportModal(true);
    } catch (err) {
      console.error('Failed to load report:', err);
    }
  };


  // Use API_BASE_URL for image/report URLs
  const getAttachmentUrl = (attachmentPath) => `${API_BASE_URL}/uploads/uploads/reports/${attachmentPath}`;

  // Client-side filtered data for live search
  const filteredActivityData = statisticsSearchQuery.trim()
    ? activityData.filter(item => {
        const q = statisticsSearchQuery.toLowerCase();
        return (
          (item.personnelName || '').toLowerCase().includes(q) ||
          (item.siteName || '').toLowerCase().includes(q) ||
          (item.siteAddress || '').toLowerCase().includes(q) ||
          (item.branchName || '').toLowerCase().includes(q) ||
          (item.regionName || '').toLowerCase().includes(q)
        );
      })
    : activityData;

  const filteredReportsData = reportsSearchQuery.trim()
    ? reportsData.filter(item => {
        const q = reportsSearchQuery.toLowerCase();
        const site = item.checkIn?.site;
        const siteFullAddress = [site?.address, site?.city, site?.state].filter(Boolean).join(', ');
        return (
          (item.user?.name || '').toLowerCase().includes(q) ||
          (site?.name || '').toLowerCase().includes(q) ||
          siteFullAddress.toLowerCase().includes(q) ||
          (item.branchName || '').toLowerCase().includes(q) ||
          (item.regionName || '').toLowerCase().includes(q)
        );
      })
    : reportsData;

  // Render filters section
  const renderFilters = (searchConfig = null) => (
    <Card className="mb-4">
      <Card.Body>
        {searchConfig && (
          <div className="stats-search-wrapper mb-3">
            <FaSearch className="stats-search-icon" />
            <Form.Control
              type="text"
              placeholder={searchConfig.placeholder || 'Search...'}
              value={searchConfig.query}
              onChange={(e) => searchConfig.setQuery(e.target.value)}
              className="stats-search-input"
            />
            {searchConfig.query && (
              <button
                className="stats-search-clear"
                onClick={() => searchConfig.setQuery('')}
                type="button"
                aria-label="Clear search"
              >
                <FaTimes />
              </button>
            )}
          </div>
        )}
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group className="mb-3 mb-md-0">
              <Form.Label>Time Range</Form.Label>
              <Form.Select
                value={customDateRange ? 'custom' : timeRange}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setCustomDateRange(true);
                  } else {
                    setCustomDateRange(false);
                    setTimeRange(e.target.value);
                  }
                }}
              >
                <option value="day">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="year">This Year</option>
                <option value="custom">Custom Date Range</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3 mb-md-0">
              <Form.Label>Branch</Form.Label>
              <Form.Select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
              >
                <option value="all">All Branches</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Region</Form.Label>
              <Form.Select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                disabled={filteredRegions.length === 0 && selectedBranch === 'all'}
              >
                <option value="all">All Regions</option>
                {filteredRegions.map(region => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3 mb-md-0">
              <Form.Label>Personnel</Form.Label>
              <Form.Select
                value={selectedPersonnel}
                onChange={(e) => setSelectedPersonnel(e.target.value)}
              >
                <option value="all">All Personnel</option>
                {personnel.map(person => (
                  <option key={person.id} value={person.id}>
                    {person.name} - {person.position}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Site</Form.Label>
              <Form.Select
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
              >
                <option value="all">All Sites</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Custom Date Range Picker */}
        {customDateRange && (
          <Row className="mt-3">
            <Col md={6}>
              <Form.Group className="mb-3 mb-md-0">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || new Date().toISOString().split('T')[0]}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  max={new Date().toISOString().split('T')[0]}
                />
              </Form.Group>
            </Col>
          </Row>
        )}

        {/* Quick Date Range Buttons */}
        {customDateRange && (
          <Row className="mt-2">
            <Col>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    setStartDate(today.toISOString().split('T')[0]);
                    setEndDate(today.toISOString().split('T')[0]);
                  }}
                >
                  Today
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    setStartDate(yesterday.toISOString().split('T')[0]);
                    setEndDate(yesterday.toISOString().split('T')[0]);
                  }}
                >
                  Yesterday
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const weekAgo = new Date(today);
                    weekAgo.setDate(today.getDate() - 7);
                    setStartDate(weekAgo.toISOString().split('T')[0]);
                    setEndDate(today.toISOString().split('T')[0]);
                  }}
                >
                  Last 7 Days
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const monthAgo = new Date(today);
                    monthAgo.setDate(today.getDate() - 30);
                    setStartDate(monthAgo.toISOString().split('T')[0]);
                    setEndDate(today.toISOString().split('T')[0]);
                  }}
                >
                  Last 30 Days
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const yearAgo = new Date(today);
                    yearAgo.setFullYear(today.getFullYear() - 1);
                    setStartDate(yearAgo.toISOString().split('T')[0]);
                    setEndDate(today.toISOString().split('T')[0]);
                  }}
                >
                  Last Year
                </Button>
              </div>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );

  // Render statistics tab content
  const renderStatisticsTab = () => (
    <>
      {/* Filters & Exports Card */}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title className="d-flex justify-content-between align-items-center flex-wrap">
            <span><FaFilter className="me-2" /> Statistics Exports</span>
            <div className="d-flex align-items-center gap-2 flex-wrap mt-2 mt-md-0">
              {exportError && (
                <Alert variant="danger" className="me-3 mb-0 py-1 px-2">
                  {exportError}
                </Alert>
              )}
              {/* Specialized Statistical Exports */}
              <Button
                variant="primary"
                size="sm"
                onClick={handleExportExecutiveSummary}
                disabled={executiveSummaryLoading || activityData.length === 0}
                title="1-2 page management summary with key metrics and insights"
              >
                {executiveSummaryLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <><FaFilePdf className="me-2" /> Statistics Summary</>
                )}
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={handleExportAnalyticsDashboard}
                disabled={analyticsExcelLoading || activityData.length === 0}
                title="Comprehensive Excel workbook with multiple data sheets and analysis"
              >
                {analyticsExcelLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <><FaFileExcel className="me-2" /> Detailed Statistics</>
                )}
              </Button>
              <Button
                variant="info"
                size="sm"
                onClick={handleExportTimesheet}
                disabled={timesheetLoading || activityData.length === 0}
                title="Daily activity timesheet with report submission tracking in calendar view"
              >
                {timesheetLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <><FaFileExcel className="me-2" /> Timesheet</>
                )}
              </Button>
            </div>
          </Card.Title>
          <div className="mt-3">
            <small className="text-muted">
              <strong>Statistics Exports:</strong> Get aggregated insights and trends.
              Timesheet shows daily submission tracking in a monthly calendar view.
            </small>
          </div>
          {renderFilters({
            query: statisticsSearchQuery,
            setQuery: setStatisticsSearchQuery,
            placeholder: 'Search by personnel name, site, branch, or region…'
          })}
        </Card.Body>
      </Card>

      {/* Summary Stats */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="dashboard-card h-100">
            <Card.Body className="text-center">
              <FaClock className="display-4 mb-3 text-primary" />
              <Card.Title>Total Hours</Card.Title>
              <h2>{typeof summaryStats.totalHours === 'number' ? summaryStats.totalHours.toFixed(1) : summaryStats.totalHours}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="dashboard-card h-100">
            <Card.Body className="text-center">
              <FaMapMarkedAlt className="display-4 mb-3 text-success" />
              <Card.Title>Total Visits</Card.Title>
              <h2>{summaryStats.totalVisits}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="dashboard-card h-100">
            <Card.Body className="text-center">
              <FaUserFriends className="display-4 mb-3 text-warning" />
              <Card.Title>Reports Submitted</Card.Title>
              <h2>{summaryStats.totalReports}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="dashboard-card h-100">
            <Card.Body className="text-center">
              <FaClock className="display-4 mb-3 text-info" />
              <Card.Title>Avg. Session Time</Card.Title>
              <h2>{typeof summaryStats.averageSessionTime === 'number' ? summaryStats.averageSessionTime.toFixed(1) : summaryStats.averageSessionTime} hrs</h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="mb-4">
        <Col lg={8}>
          <Card className="chart-container h-100">
            <Card.Body>
              <Card.Title>Activity Over Time</Card.Title>
              <div style={{ height: '300px' }}>
                {chartData.activity.labels && chartData.activity.datasets && (
                  <Bar
                    data={chartData.activity}
                    options={{
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          labels: {
                            color: chartColors.textColor,
                          }
                        },
                        tooltip: {
                          backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                          titleColor: chartColors.textColor,
                          bodyColor: chartColors.textColor,
                          borderColor: chartColors.borderColor,
                          borderWidth: 1,
                        }
                      },
                      scales: {
                        x: {
                          ticks: {
                            color: chartColors.textColor,
                          },
                          grid: {
                            color: chartColors.gridColor,
                          }
                        },
                        y: {
                          type: 'linear',
                          display: true,
                          position: 'left',
                          ticks: {
                            color: chartColors.textColor,
                          },
                          grid: {
                            color: chartColors.gridColor,
                          },
                          title: {
                            display: true,
                            text: 'Site Visits',
                            color: chartColors.textColor,
                          }
                        },
                        y1: {
                          type: 'linear',
                          display: true,
                          position: 'right',
                          ticks: {
                            color: chartColors.textColor,
                          },
                          grid: {
                            drawOnChartArea: false,
                            color: chartColors.gridColor,
                          },
                          title: {
                            display: true,
                            text: 'Hours',
                            color: chartColors.textColor,
                          }
                        },
                      }
                    }}
                  />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="chart-container h-100">
            <Card.Body>
              <Card.Title>Site Distribution</Card.Title>
              <div style={{ height: '300px', display: 'flex', justifyContent: 'center' }}>
                {chartData.siteDistribution.labels && (
                  <Pie
                    data={chartData.siteDistribution}
                    options={{
                      maintainAspectRatio: false,
                      responsive: true,
                      plugins: {
                        legend: {
                          labels: {
                            color: chartColors.textColor,
                          }
                        },
                        tooltip: {
                          backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                          titleColor: chartColors.textColor,
                          bodyColor: chartColors.textColor,
                          borderColor: chartColors.borderColor,
                          borderWidth: 1,
                        }
                      }
                    }}
                  />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card className="chart-container">
            <Card.Body>
              <Card.Title>Hours by Personnel</Card.Title>
              <div style={{ height: '300px' }}>
                {chartData.hoursByPersonnel.labels && (
                  <Bar
                    data={chartData.hoursByPersonnel}
                    options={{
                      maintainAspectRatio: false,
                      indexAxis: 'y',
                      plugins: {
                        legend: {
                          labels: {
                            color: chartColors.textColor,
                          }
                        },
                        tooltip: {
                          backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                          titleColor: chartColors.textColor,
                          bodyColor: chartColors.textColor,
                          borderColor: chartColors.borderColor,
                          borderWidth: 1,
                        }
                      },
                      scales: {
                        x: {
                          ticks: {
                            color: chartColors.textColor,
                          },
                          grid: {
                            color: chartColors.gridColor,
                          }
                        },
                        y: {
                          ticks: {
                            color: chartColors.textColor,
                          },
                          grid: {
                            color: chartColors.gridColor,
                          }
                        }
                      }
                    }}
                  />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Detailed Activity Table */}
      <Card className="table-container">
        <Card.Body>
          <Card.Title>Detailed Activity</Card.Title>
          <div className="table-responsive">
            <Table striped hover>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Personnel</th>
                  <th>Site</th>
                  <th>Location</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Hours</th>
                  <th>Report</th>
                  <th>Breaks</th>
                  <th>Tracking</th>
                </tr>
              </thead>
              <tbody>
                {filteredActivityData.map(activity => (
                  <tr key={activity.id}>
                    <td>{format(new Date(activity.checkInTime), 'yyyy-MM-dd')}</td>
                    <td>{activity.personnelName}</td>
                    <td>{activity.siteName}</td>
                    <td>{activity.siteAddress}</td>
                    <td>{format(new Date(activity.checkInTime), 'hh:mm a')}</td>
                    <td>{activity.checkOutTime ? format(new Date(activity.checkOutTime), 'hh:mm a') : '—'}</td>
                    <td>{activity.hoursSpent ?? '—'}</td>
                    <td>
                      {activity.reportSubmitted ? (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewActivityReport(activity)}
                        >
                          <FaEye className="me-1" /> View Report
                        </Button>
                      ) : (
                        <Badge bg="danger">Missing</Badge>
                      )}
                    </td>
                    <td>
                      {activity.breaks?.length > 0 ? (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleShowBreaks(activity)}
                        >
                          View
                        </Button>
                      ) : (
                        <Badge bg="secondary">None</Badge>
                      )}
                    </td>
                    <td>
                      {activity.tracking?.length > 0 ? (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleShowTracking(activity)}
                        >
                          View
                        </Button>
                      ) : (
                        <Badge bg="secondary">—</Badge>
                      )}
                    </td>
                  </tr>

                ))}
                {filteredActivityData.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center">
                      {statisticsSearchQuery.trim()
                        ? `No results match "${statisticsSearchQuery}"."`
                        : 'No activity data found for the selected filters.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </>
  );

  // Render reports tab content
  const renderReportsTab = () => (
    <>
      {/* Reports Exports Card */}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title className="d-flex justify-content-between align-items-center flex-wrap">
            <span><FaFileAlt className="me-2" /> Report Exports</span>
            <div className="d-flex align-items-center gap-2 flex-wrap mt-2 mt-md-0">
              {reportExportError && (
                <Alert variant="danger" className="me-3 mb-0 py-1 px-2">
                  {reportExportError}
                </Alert>
              )}
              {sitesReportsExportError && (
                <Alert variant="danger" className="me-3 mb-0 py-1 px-2">
                  {sitesReportsExportError}
                </Alert>
              )}
              <Button
                variant="success"
                size="sm"
                onClick={handleExportSitesReportsPdf}
                disabled={sitesReportsExportLoading || reportsData.length === 0}
                title="Export all reports as PDF"
              >
                {sitesReportsExportLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <><FaFilePdf className="me-2" /> Export as PDF</>
                )}
              </Button>
              <Button
                variant="info"
                size="sm"
                onClick={handleExportSitesReportsExcel}
                disabled={sitesReportsExportLoading || reportsData.length === 0}
                title="Export all reports as Excel spreadsheet"
              >
                {sitesReportsExportLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <><FaFileExcel className="me-2" /> Export as Excel</>
                )}
              </Button>
            </div>
          </Card.Title>
          <div className="mt-3">
            <small className="text-muted">
              <strong>Report Exports:</strong> Get detailed visit documentation with photos and timestamps.
              Export individual reports from the table below, or use the bulk export buttons above.
            </small>
          </div>
          {renderFilters({
            query: reportsSearchQuery,
            setQuery: setReportsSearchQuery,
            placeholder: 'Search by personnel name, site, branch, or region…'
          })}
        </Card.Body>
      </Card>

      {/* Reports Table */}
      <Card className="table-container">
        <Card.Body>
          <Card.Title>Personnel Reports</Card.Title>
          <div className="table-responsive">
            <Table striped hover>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Personnel</th>
                  <th>Site</th>
                  <th>Report Title</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReportsData.map(report => (
                  <tr key={report.id}>
                    <td>{new Date(report.createdAt).toLocaleDateString()}</td>
                    <td>{report.user ? report.user.name : 'Unknown'}</td>
                    <td>{report.checkIn && report.checkIn.site ? report.checkIn.site.name : 'Unknown'}</td>
                    <td>{'Site Visit Report'}</td>
                    <td>
                      <Badge bg="success">Completed</Badge>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleViewReport(report)}
                      >
                        <FaEye className="me-1" /> View
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => handleExportReportPDF(report.id)}
                        disabled={reportExportLoading}
                      >
                        {reportExportLoading ? (
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        ) : (
                          <><FaDownload className="me-1" /> PDF</>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredReportsData.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center">
                      {reportsSearchQuery.trim()
                        ? `No results match "${reportsSearchQuery}"."`
                        : 'No reports found for the selected filters.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Report Modal */}
      <Modal show={showReportModal} onHide={() => setShowReportModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Report Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReport && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Personnel:</strong> {selectedReport.user ? selectedReport.user.name : 'Unknown'}</p>
                  <p><strong>Site:</strong> {selectedReport.checkIn && selectedReport.checkIn.site ? selectedReport.checkIn.site.name : 'Unknown'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Date:</strong> {new Date(selectedReport.createdAt).toLocaleDateString()}</p>
                  <p><strong>Time:</strong> {new Date(selectedReport.createdAt).toLocaleTimeString()}</p>
                </Col>
              </Row>
              <hr />
              <h5>Report Content</h5>
              <p>{selectedReport.content}</p>

              {selectedActivity?.attachmentPath && (
                <div className="mt-4">
                  <h5>Attachment</h5>

                  {selectedActivity.attachmentPath.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                    <img
                      src={`${API_BASE_URL}/uploads/uploads/reports/${selectedActivity.attachmentPath}`}
                      alt="Activity Report Attachment"
                      style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '10px' }}
                    />
                  ) : (
                    <p className="text-muted">Attachment is not an image.</p>
                  )}

                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() =>
                      window.open(
                        `${API_BASE_URL}/uploads/uploads/reports/${selectedActivity.attachmentPath}`,
                        '_blank'
                      )
                    }
                  >
                    <FaFileAlt className="me-2" /> Open Attachment
                  </Button>
                </div>
              )}


            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReportModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => selectedReport && handleExportReportPDF(selectedReport.id)}
            disabled={reportExportLoading}
          >
            {reportExportLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Exporting...
              </>
            ) : (
              <><FaFilePdf className="me-2" /> Export as PDF</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Activity Report Modal */}
      <Modal show={showActivityReportModal} onHide={() => setShowActivityReportModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Check-in Report Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedActivity && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Personnel:</strong> {selectedActivity.personnelName}</p>
                  <p><strong>Position:</strong> {selectedActivity.position}</p>
                  <p><strong>Site:</strong> {selectedActivity.siteName}</p>
                  <p><strong>Location:</strong> {selectedActivity.siteLocation}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Check-in:</strong> {new Date(selectedActivity.checkInTime).toLocaleString()}</p>
                  <p><strong>Check-out:</strong> {selectedActivity.checkOutTime ? new Date(selectedActivity.checkOutTime).toLocaleString() : 'Not checked out'}</p>
                  <p><strong>Hours Spent:</strong> {selectedActivity.hoursSpent || 'N/A'}</p>
                </Col>
              </Row>
              <hr />
              <h5>Report Content</h5>
              <div className="p-3 border rounded bg-light">
                {selectedActivity.reportContent ? (
                  <p style={{ whiteSpace: 'pre-wrap' }}>{selectedActivity.reportContent}</p>
                ) : (
                  <p className="text-muted">No report content available.</p>
                )}
              </div>

              {/* Add this section for the image */}
              {selectedReport?.checkOutPhotos?.length > 0 && (
                <div className="mt-4">
                  <h5>Check-out Photos</h5>
                  <Row>
                    {selectedReport.checkOutPhotos.map((url, index) => (
                      <Col md={4} key={index} className="mb-3">
                        <img
                          src={`${API_BASE_URL}${url}`}
                          alt={`Check-out ${index + 1}`}
                          className="img-fluid rounded border"
                          onError={(e) => {
                            e.target.src = 'https://placehold.co/400x300?text=Image+Not+Found';
                          }}
                        />
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mt-2"
                          onClick={() => window.open(`${API_BASE_URL}${url}`, '_blank')}
                        >
                          <FaFileAlt className="me-2" /> Open Full Image
                        </Button>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}


            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActivityReportModal(false)}>
            Close
          </Button>
          {selectedActivity?.reportId && (
            <Button
              variant="primary"
              onClick={() => handleExportReportPDF(selectedActivity.reportId)}
              disabled={reportExportLoading}
            >
              {reportExportLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  Exporting...
                </>
              ) : (
                <><FaDownload className="me-1" /> Export as PDF</>
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>


      <Modal show={showBreaksModal} onHide={() => setShowBreaksModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Break Periods</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBreakActivity?.breaks.map((b, i) => (
            <div key={i}>
              <p><strong>Start:</strong> {new Date(b.startTime).toLocaleString()}</p>
              <p><strong>End:</strong> {b.endTime ? new Date(b.endTime).toLocaleString() : 'Ongoing'}</p>
              <p><strong>Location:</strong> {b.endLocation || b.startLocation || 'N/A'}</p>
              <hr />
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBreaksModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showTrackingModal} onHide={() => setShowTrackingModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Minute Tracking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTrackingActivity?.tracking.map((track, i) => (
            <div key={i}>
              <p><strong>Time:</strong> {new Date(track.createdAt).toLocaleString()}</p>
              <p><strong>Location:</strong> {track.location || "N/A"}</p>
              <hr />
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTrackingModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>



    </>
  );

  // Render Personnel Performance tab content
  const renderPersonnelPerformanceTab = () => (
    <>
      {/* Filters Card */}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title className="d-flex justify-content-between align-items-center">
            <span><FaFilter className="me-2" /> Filters</span>
            <div className="d-flex align-items-center gap-2">
              {personnelPerformanceExportError && (
                <Alert variant="danger" className="me-3 mb-0 py-1 px-2">
                  {personnelPerformanceExportError}
                </Alert>
              )}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleExportPersonnelPerformancePdf}
                disabled={personnelPerformanceExportLoading || personnelPerformanceData.length === 0}
              >
                {personnelPerformanceExportLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <><FaFilePdf className="me-2" /> Export as PDF</>
                )}
              </Button>
              <Button
                variant="outline-success"
                size="sm"
                onClick={handleExportPersonnelPerformanceExcel}
                disabled={personnelPerformanceExportLoading || personnelPerformanceData.length === 0}
              >
                {personnelPerformanceExportLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <><FaFileExcel className="me-2" /> Export as Excel</>
                )}
              </Button>
            </div>
          </Card.Title>
          {renderFilters()}
        </Card.Body>
      </Card>

      {/* Personnel Performance Summary Section */}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title className="d-flex justify-content-between align-items-center">
            <span><FaUserFriends className="me-2" /> Personnel Performance Summary</span>
            <div className="text-muted">
              <small>Total Personnel: {personnelPerformanceData.length}</small>
            </div>
          </Card.Title>
          <p className="text-muted mb-3">
            Comprehensive performance analysis of all personnel including total visits, hours worked, reports submitted, and performance ratings.
            <br />
            <small>Report generated on: {new Date().toLocaleDateString()}</small>
          </p>

          {/* Personnel Performance Table */}
          <div className="table-responsive">
            <Table striped hover>
              <thead>
                <tr>
                  <th>Personnel Name</th>
                  <th>Position</th>
                  <th>Total Visits</th>
                  <th>Total Hours</th>
                  <th>Reports Submitted</th>
                  <th>Avg Hours/Visit</th>
                  <th>Performance Rating</th>
                </tr>
              </thead>
              <tbody>
                {currentPersonnelData.length > 0 ? (
                  currentPersonnelData.map((personnel) => (
                    <tr key={personnel.id}>
                      <td>{personnel.name}</td>
                      <td>{personnel.position || 'N/A'}</td>
                      <td>{personnel.totalVisits}</td>
                      <td>{personnel.totalHours.toFixed(1)}</td>
                      <td>{personnel.totalReports}</td>
                      <td>{personnel.avgHoursPerVisit.toFixed(1)}</td>
                      <td>
                        <Badge bg={personnel.ratingColor}>{personnel.performanceRating}</Badge>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">No personnel performance data available for the selected filters.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {personnelPerformanceData.length > itemsPerPage && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted">
                Showing {startIndex + 1} to {Math.min(endIndex, personnelPerformanceData.length)} of {personnelPerformanceData.length} personnel
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <div className="d-flex align-items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className="px-3"
                    >
                      {page}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );



  return (
    <Container className="py-4 statistics-container">
      <h1 className="mb-4">Statistics & Reports</h1>
      <p className="lead text-muted mb-4">
        View performance metrics, trends, and export detailed operational reports
      </p>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => {
          console.log('Tab selected:', k);
          setActiveTab(k);
        }}
        className="mb-4"
      >
        <Tab eventKey="statistics" title={<span><FaFileAlt className="me-2" />Statistics</span>}>
          {renderStatisticsTab()}
        </Tab>
        <Tab eventKey="personnel-performance" title={<span><FaUserFriends className="me-2" />Personnel Performance</span>}>
          {renderPersonnelPerformanceTab()}
        </Tab>
        <Tab eventKey="reports" title={<span><FaFilePdf className="me-2" />Reports</span>}>
          {renderReportsTab()}
        </Tab>
        <Tab eventKey="unvisited" title="Sites Statistics">
          {/* Filters Card */}
          <Card className="mb-4">
            <Card.Body>
              <Card.Title className="d-flex justify-content-between align-items-center">
                <span><FaFilter className="me-2" /> Filters</span>
              </Card.Title>
              {renderFilters()}

              <Row className="mt-3 align-items-end">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Site Visit Status</Form.Label>
                    <Form.Select
                      value={siteVisitStatus}
                      onChange={(e) => setSiteVisitStatus(e.target.value)}
                    >
                      <option value="all">All Sites ({unvisitedSites.length})</option>
                      <option value="visited">Visited ({visitedCount})</option>
                      <option value="unvisited">Unvisited ({unvisitedCount})</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {selectedPersonnel !== 'all'
                        ? "Scoped to the selected personnel's assigned sites and check-ins."
                        : 'Scoped to all sites in your organization.'}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={8} className="d-flex align-items-end justify-content-end">
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-danger"
                      onClick={handleExportUnvisitedSitesPdf}
                      disabled={unvisitedExportLoading || displayedSites.length === 0}
                    >
                      {unvisitedExportLoading ? <Spinner animation="border" size="sm" /> : <FaFilePdf className="me-2" />}
                      Export PDF
                    </Button>
                    <Button
                      variant="outline-success"
                      onClick={handleExportUnvisitedSitesExcel}
                      disabled={unvisitedExportLoading || displayedSites.length === 0}
                    >
                      {unvisitedExportLoading ? <Spinner animation="border" size="sm" /> : <FaFileExcel className="me-2" />}
                      Export Excel
                    </Button>
                  </div>
                </Col>
              </Row>

              {unvisitedExportError && (
                <Alert variant="danger" className="mt-3" onClose={() => setUnvisitedExportError('')} dismissible>
                  {unvisitedExportError}
                </Alert>
              )}
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Header className="bg-white py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-primary fw-bold">
                  <FaMapMarkedAlt className="me-2" />
                  {siteVisitStatus === 'visited'
                    ? 'Visited Sites'
                    : siteVisitStatus === 'unvisited'
                      ? 'Unvisited Sites'
                      : 'All Sites'} ({displayedSites.length})
                  {selectedPersonnel !== 'all' && (
                    <small className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>
                      &mdash; assigned to {(personnel.find(p => String(p.id) === String(selectedPersonnel)) || {}).name || 'selected personnel'}
                    </small>
                  )}
                </h5>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">Loading sites...</p>
                </div>
              ) : displayedSites.length > 0 ? (
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th>Site Name</th>
                        <th>Location</th>
                        <th>Visits</th>
                        <th>Contact Person</th>
                        <th>Contact Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedSites.map((site) => (
                        <tr key={site.id}>
                          <td className="fw-medium">{site.name}</td>
                          <td>
                            <div>{site.address}</div>
                            <small className="text-muted">{site.city}, {site.state}</small>
                          </td>
                          <td>
                            {(() => {
                              const count = Number(site.visitCount) || 0;
                              let variant = 'success';
                              let text = 'Excellent';
                              if (count === 0) {
                                variant = 'danger';
                                text = 'Not visited';
                              } else if (count < 5) {
                                variant = 'danger';
                                text = 'Critical';
                              } else if (count < 10) {
                                variant = 'warning';
                                text = 'Warning';
                              } else if (count < 15) {
                                variant = 'info';
                                text = 'Caution';
                              } else if (count < 20) {
                                variant = 'primary';
                                text = 'Good';
                              }

                              return (
                                <Badge bg={variant} text={variant === 'warning' || variant === 'info' ? 'dark' : 'white'}>
                                  {count === 0 ? text : `${count} visits (${text})`}
                                </Badge>
                              );
                            })()}
                          </td>
                          <td>{site.contactPersonName || '-'}</td>
                          <td>{site.contactPhone || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <FaMapMarkedAlt size={48} className="text-muted opacity-25" />
                  </div>
                  <h5 className="text-muted">No sites found</h5>
                  <p className="text-muted small">
                    No sites matching the selected criteria were found.
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading data...</p>
        </div>
      )}
    </Container>
  );
};

export default AdminStatistics;
